import React, { useState, useRef, useMemo } from 'react';
import { Calculator, PanelLeftClose, PanelLeftOpen, X, Search, Lock } from 'lucide-react';
import { cn } from '../lib/utils';
import { MENU_ITEMS } from '../constants';
import { useAuth } from '../context/AuthContext';
import { useEntitlement } from '../context/EntitlementContext';
import { useFeatureToggle } from '../context/FeatureToggleContext';

const MENU_FEATURE: Record<string, string> = {
  'accounts-receivable': 'customers',
  'accounts-payable': 'expenses',
  'general-ledger': 'accounts',
  'inventory': 'inventory',
  'fixed-assets': 'fixed_assets',
  'payroll': 'payroll',
  'ai-assistant': 'ai_assistant',
};

// SubItems that should be hidden when their feature toggle is off
const TOGGLE_SUB_ITEMS: Record<string, string> = {
  '#plan-billing': 'billing',
  '#pricing': 'pricing',
};

interface SidebarProps {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  currentPath: string;
  setIsDashboardView: (view: boolean) => void;
  user: any;
}

export default function Sidebar({
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  searchQuery,
  setSearchQuery,
  currentPath,
  setIsDashboardView,
  user
}: SidebarProps) {
  const { can } = useAuth();
  const { canUse } = useEntitlement();
  const { isEnabled } = useFeatureToggle();
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const [hoveredMenuId, setHoveredMenuId] = useState<string | null>(null);
  const [menuTop, setMenuTop] = useState<number>(0);
  const hoverTimeoutRef = useRef<any>(null);

  const visibleMenuItems = useMemo(() =>
    MENU_ITEMS.filter(item => {
      if (!item.requiredPermission) return true;
      return can(item.requiredPermission.resource, item.requiredPermission.action);
    }).map(item => {
      // Filter out subItems that are toggled off
      if (item.subItems) {
        const filteredSubs = item.subItems.filter(sub => {
          const toggleKey = TOGGLE_SUB_ITEMS[sub.href || ''];
          return !toggleKey || isEnabled(toggleKey);
        });
        return { ...item, subItems: filteredSubs };
      }
      return item;
    }),
  [can, isEnabled]);

  const toggleSubmenu = (id: string) => {
    setOpenSubmenus(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#0f172a] text-slate-900 dark:text-slate-100 border-r border-slate-200 dark:border-slate-800 transition-colors duration-300" role="navigation" aria-label="Main navigation">
      <div className="p-6 flex items-center justify-between">
        <div 
          onClick={() => {
            window.location.hash = '#';
            setIsDashboardView(false);
          }}
          className={cn("flex items-center gap-3 overflow-hidden transition-all duration-300 cursor-pointer hover:opacity-80", isSidebarCollapsed ? "w-0 opacity-0" : "w-auto opacity-100")}
        >
          <div className="bg-primary p-2 rounded-lg shrink-0">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-display font-bold whitespace-nowrap text-slate-900 dark:text-white">Majelani</span>
        </div>
        <button 
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors hidden lg:block text-slate-500 dark:text-slate-400"
        >
          {isSidebarCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
        </button>
        <button 
          onClick={() => setIsMobileMenuOpen(false)}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors lg:hidden text-slate-500 dark:text-slate-400"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className={cn("px-4 mb-4 transition-all duration-300", isSidebarCollapsed ? "opacity-0 h-0 overflow-hidden" : "opacity-100 h-auto")}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search modules..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search navigation modules"
            className="w-full bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-full py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-slate-900 dark:text-white placeholder:text-slate-500 dark:placeholder:text-slate-400"
          />
        </div>
      </div>

      <nav className="flex-grow overflow-y-auto px-3 py-2 custom-scrollbar" aria-label="Application modules">
        <ul className="space-y-1" role="list">
          {visibleMenuItems.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase())).map((item) => (
            <li 
              key={item.id} 
              className="group"
              onMouseEnter={(e) => {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                  hoverTimeoutRef.current = null;
                }
                if (isSidebarCollapsed) {
                  setHoveredMenuId(item.id);
                  const rect = e.currentTarget.getBoundingClientRect();
                  const menuHeight = 300;
                  const windowHeight = window.innerHeight;
                  
                  if (rect.top + menuHeight > windowHeight) {
                    setMenuTop(Math.max(10, windowHeight - menuHeight - 20));
                  } else {
                    setMenuTop(rect.top);
                  }
                }
              }}
              onMouseLeave={() => {
                hoverTimeoutRef.current = setTimeout(() => {
                  setHoveredMenuId(null);
                }, 300);
              }}
            >
              <div 
                className={cn(
                  "flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-200",
                  "hover:bg-slate-100 dark:hover:bg-slate-800",
                  (item.href === currentPath || item.subItems?.some(sub => sub.href === currentPath)) && "bg-primary/10 text-primary"
                )}
                role="button"
                tabIndex={0}
                aria-expanded={item.subItems ? openSubmenus.includes(item.id) : undefined}
                aria-label={item.label}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (item.subItems) { toggleSubmenu(item.id); } else { window.location.hash = item.href; setIsMobileMenuOpen(false); } } }}
                onClick={() => {
                  setIsDashboardView(true);
                  if (item.subItems) {
                    toggleSubmenu(item.id);
                  } else {
                    window.location.hash = item.href;
                    setIsMobileMenuOpen(false);
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <item.icon 
                    className={cn(
                      "w-5 h-5 shrink-0 transition-colors", 
                      (item.href === currentPath || item.subItems?.some(sub => sub.href === currentPath)) ? "text-primary" : "text-slate-500 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white"
                    )} 
                    style={{ color: (item.href === currentPath || item.subItems?.some(sub => sub.href === currentPath)) ? undefined : item.color }}
                  />
                  {!isSidebarCollapsed && (
                    <span className={cn("text-sm font-medium whitespace-nowrap", (item.href === currentPath || item.subItems?.some(sub => sub.href === currentPath)) ? "text-primary font-bold" : "text-slate-700 dark:text-slate-300")}>{item.label}</span>
                  )}
                  {!isSidebarCollapsed && MENU_FEATURE[item.id] && !canUse(MENU_FEATURE[item.id]) && (
                    <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-auto mr-1" />
                  )}
                </div>
                {!isSidebarCollapsed && item.subItems && (
                  <div className={cn(
                    "w-2 h-2 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-4 transition-transform duration-300",
                    openSubmenus.includes(item.id) ? "rotate-90 border-l-slate-600 dark:border-l-slate-300" : "border-l-slate-400 dark:border-l-slate-500"
                  )} />
                )}
              </div>
              
              {!isSidebarCollapsed && item.subItems && (
                <div className={cn(
                  "submenu-transition overflow-hidden",
                  openSubmenus.includes(item.id) ? "max-h-[1000px] opacity-100 mt-1" : "max-h-0 opacity-0"
                )}>
                  <ul className="pl-12 py-2 space-y-0.5 bg-slate-50 dark:bg-black/20 rounded-lg mx-2 border border-slate-100 dark:border-transparent max-h-[50vh] overflow-y-auto custom-scrollbar">
                    {item.subItems.map((sub, idx) => (
                      sub.separator ? (
                        <li key={`sep-${idx}`} className="px-4 pt-3 pb-1 first:pt-1">
                          <p className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">{sub.label}</p>
                          <div className="h-px bg-slate-200 dark:bg-slate-700/30 mt-1" />
                        </li>
                      ) : (
                      <li key={idx} className="relative">
                        {sub.href === currentPath && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full shadow-[0_0_12px_rgba(37,99,235,0.5)]" />
                        )}
                        <a 
                          href={sub.href}
                          onClick={() => { setIsDashboardView(true); setIsMobileMenuOpen(false); }}
                          className={cn(
                            "block py-2 text-sm transition-all px-4 rounded-md",
                            sub.href === currentPath 
                              ? "text-primary font-bold bg-primary/10 dark:bg-primary/5" 
                              : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5"
                          )}
                        >
                          {sub.label}
                        </a>
                      </li>
                      )
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-4 border-t border-slate-200 dark:border-slate-800">
        <div className={cn("flex items-center gap-3 overflow-hidden transition-all duration-300", isSidebarCollapsed ? "justify-center" : "px-2")}>
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-xs font-bold shrink-0 text-white">
            {(user?.name || user?.user_metadata?.full_name) ? (user.name || user.user_metadata?.full_name).split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
          </div>
          {!isSidebarCollapsed && (
            <div className="overflow-hidden">
              <p className="text-sm font-bold truncate text-slate-900 dark:text-white">{user?.name || user?.user_metadata?.full_name || 'User'}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email || 'Admin Account'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
